<template>
  <dashboard-layout-template 
    :showQuickSetup="showQuickSetup" 
    :showTransitionModal="showTransitionModal"
    :transitionModalFadeIn="true" 
    :transitionModalFadeOut="true"
  >
    <base-header 
      slot="header" 
      :heading="this.$t('Home')" 
      :description="''"
    >
      <div slot="search" class="search-wrapper">
        <div class="input-group append-group-wrapper search">
          <input type="text" class="form-control" :placeholder="this.$t('SearchHelper')" aria-label="Search Orders" v-model="searchTerm" @keyup.enter.prevent="searchOrders">
          <div class="input-group-append">
            <span class="input-group-text"></span>
          </div>
        </div>
      </div>
      <div slot="action">
        <div class="btn-group btn-group-nav" role="group">
          <div class="btn-group btn-group-nav" role="group">
            <router-link v-if="enableNewIntegrationsUi" class="btn btn-primary btn-first dropdown-menu-right" to="/integrations/manual-order-sync">
              {{ $t('SyncOrders') }}
            </router-link>
            <a v-else class="btn btn-primary btn-first dropdown-menu-right" :href="syncLink"> {{ this.$t('SyncOrders') }} </a>
            <router-link class="btn btn-primary dropdown-menu-right" :to="{ name: 'ImportOrder' }"> {{ this.$t('ImportOrders') }} </router-link>
            <a class="btn btn-primary btn-last" @click.prevent="routeToCreateOrder" href="#"> {{ this.$t('NewOrder') }}</a>
          </div>
        </div>
      </div>
    </base-header>

    <template slot="notifications">
      <on-hold-orders-notifications-panel classes="on-hold-notifications-panel" />
      <credit-card-processing-fee-banner />
      <setup-mfa-banner />
      <subaccount-invitation-banner />
    </template>

    <template slot="panel">
      <welcome-panel :panelSize="'large'" :loading="initializing" v-if="!merchantDashboardV2FeatureFlag"></welcome-panel>
      <notifications-panel :panelSize="'large'" :loading="initializing" v-if="merchantDashboardV2FeatureFlag"></notifications-panel>
      <announcements-panel v-if="!isPoweredBySubAccountUser" :panelSize="'small'" :loading="initializing"></announcements-panel>
      <fulfillment-metrics-panel :panelSize="'large'" :loading="initializing" :panelFocus="true"></fulfillment-metrics-panel>
      <order-status-panel :panelSize="'small'" :loading="initializing"></order-status-panel>
      <inventory-reorder-panel :panelSize="'large'" :loading="initializing"></inventory-reorder-panel>
      <dashboard-carousel-panel v-if="!isPoweredBySubAccountUser" :panelSize="'small'" :loading="initializing"></dashboard-carousel-panel>
    </template>
  </dashboard-layout-template>
</template>

<script>

import WelcomePanel from '@/components/dashboard/panels/WelcomePanel'
import AnnouncementsPanel from '@/components/dashboard/panels/AnnouncementsPanel'
import OrderStatusPanel from '@/components/dashboard/panels/OrderStatusPanel'
import DashboardCarouselPanel from '@/components/dashboard/panels/DashboardCarouselPanel'
import FulfillmentMetricsPanel from '@/components/dashboard/panels/FulfillmentMetricsPanel'
import InventoryReorderPanel from '@/components/dashboard/panels/InventoryReorderPanel'
import NotificationsPanel from '@/components/dashboard/panels/NotificationsPanel'
import DashboardLayoutTemplate from '@/components/layouts/DashboardLayoutTemplate'
import BaseRoutesEnum from '@/enums/modules/routes/base'
import NotificationsRoutesEnum from '@/enums/modules/routes/notifications'
import { CreateOrderEnum } from '@enums/modules/routes'
import OnHoldOrdersNotificationsPanel from '@/components/notifications/panels/OnHoldOrders'
import ShipbobAppSettings from '@/appSettings/appSetting'
import SubaccountInvitationBanner from '@/components/notifications/banners/SubaccountInvitationBanner'
import SetupMfaBanner from '@/components/notifications/banners/SetupMfaBanner'
import CreditCardProcessingFeeBanner from '@/components/notifications/panels/CreditCardProcessingFeeBanner'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Dashboard',
  components: {
    DashboardLayoutTemplate,
    AnnouncementsPanel,
    OrderStatusPanel,
    DashboardCarouselPanel,
    FulfillmentMetricsPanel,
    InventoryReorderPanel,
    NotificationsPanel,
    WelcomePanel,
    OnHoldOrdersNotificationsPanel,
    SubaccountInvitationBanner,
    SetupMfaBanner,
    'credit-card-processing-fee-banner': CreditCardProcessingFeeBanner
  },
  inject: ['isPoweredBySubAccountUser'],
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.$shipBobStore.dispatch('dashboard/getDashboardSettings').then(res => {
        vm.initialize()
      })
    })
    next()
  },
  data() {
    return {
      initializing: true,
      showQuickSetup: true,
      showTransitionModal: false,
      searchTerm: ''
    }
  },
  computed: {
    syncLink() {
      return this.$sbAppSettings.photoPrint('/Integrations/')
    },
    dashboardSettings() {
      return this.$shipBobStore.getters['dashboard/dashboardSettings']
    },
    merchantDashboardV2FeatureFlag() {
      return this.$shipBobStore.getters.enableMerchantDashboardV2
    },
    merchantB2BFeatureFlag() {
      return this.$shipBobStore.getters.enableMerchantB2BApp
    },
    enableNewIntegrationsUi: function () {
      return this.$shipBobStore.getters.enableNewIntegrationsUi
    }
  },
  destroyed() {
    const routeName = this.$route.name
    if (routeName !== BaseRoutesEnum.SetupDashboard && routeName !== NotificationsRoutesEnum.AllNotifications) {
      this.$shipBobStore.unregisterModule('dashboard')
    }
  },
  methods: {
    initialize() {
      this.initializing = false
    },
    searchOrders() {
      this.$router.push({ path: 'Orders/Search/' + this.searchTerm })
    },
    routeToCreateOrder() {
      window.localStorage.setItem('VueOrdersRedirect', true)
      if (this.merchantB2BFeatureFlag) {
        const b2bCreateOrderTypePath = `${ShipbobAppSettings.vueRouterB2BBase}#/CreateOrder/OrderType`
        window.location = (b2bCreateOrderTypePath)
      } else {
        this.$router.push({ name: CreateOrderEnum.OrderType })
      }
    }
  },
  i18n: {
    messages: {
      'en-US': {
        SearchHelper: 'Search orders by email, shipment ID, recipient name...',
        Dashboard: 'Dashboard',
        SyncOrders: 'Sync Orders',
        ImportOrders: 'Import Orders',
        NewOrder: 'New Order'
      }
    }
  }
}
</script>
<style scoped lang='scss'>
@import '@/../assets/scss/shipbob-custom/components/dashboard/_dashboard.scss';
</style>